import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { reactQueryRequest } from 'api/makeQuery'
import { API_FETCH_LIMIT } from 'constants/apiFetchLimits'
import { addMinutes, formatISO } from 'date-fns'
import { uniqBy } from 'lodash'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import Pusher from 'singletons/Pusher'
import User from 'singletons/User'
import {
  CreateAvailabilityFormValues,
  ProviderScheduleSlot,
  ProviderScheduleSlotsFilter,
} from '@ui-components-3/provider-schedule-calendar'

export const useScheduleApi = ({
  onSuccessCreateOrUpdate,
  onErrorCreateOrUpdate,
  slotsFilter,
  shouldFetchSlots = true,
  shouldFetchVisits = true,
}: {
  onSuccessCreateOrUpdate?: () => void
  onErrorCreateOrUpdate?: () => void
  slotsFilter: ProviderScheduleSlotsFilter
  shouldFetchSlots?: boolean
  shouldFetchVisits?: boolean
}) => {
  const [isWaitingForPusherEvent, setIsWaitingForPusherEvent] = useState(false)
  const queryClient = useQueryClient()

  const { mutate: submitSchedule, isPending: isScheduleMutationPending } = useMutation({
    mutationFn: async (values: CreateAvailabilityFormValues & { id?: string }) => {
      setIsWaitingForPusherEvent(true)
      const queryKey = values?.id ? 'putProviderScheduleV3' : 'postProviderSchedule'

      return await reactQueryRequest(queryKey, values)
    },
    onError: () => {
      setIsWaitingForPusherEvent(false)
      onErrorCreateOrUpdate?.()
    },
  })

  const { providerId } = User

  const { data: pendingVisits, isFetching: isFetchingPendingVisits } = useQuery({
    enabled: !!providerId && !!slotsFilter?.startDate && !!slotsFilter?.endDate && !!shouldFetchVisits,
    queryKey: ['getPendingVisitsForSchedule', { ...(slotsFilter || {}), providerId }],
    queryFn: async () => {
      const data = await reactQueryRequest('getPendingVisits', {
        filter: {
          provider_id: providerId,
          status: ['pending', 'completed', 'in-review'],
          scheduled_type: ['scheduled'],
        },
        gt: {
          scheduled_at: formatISO(new Date(slotsFilter?.startDate)),
        },
        lt: {
          scheduled_at: formatISO(new Date(slotsFilter?.endDate)),
        },
        page: {
          skip: 0,
          limit: API_FETCH_LIMIT,
        },
      })

      return (data?.paginated || [])?.map<ProviderScheduleSlot & { isFakeSlot: boolean }>((visit) => {
        const { pendingVisitId, initial, scheduledDuration, scheduledAt, createdAt, updatedAt } = visit

        return {
          isFakeSlot: true,
          id: pendingVisitId,
          blockId: '',
          booked: true,
          duration: scheduledDuration,
          bookingType: initial ? 'new' : 'follow_up',
          startTime: scheduledAt,
          pendingVisitId,
          providerId,
          createdAt,
          updatedAt,
        }
      })
    },
  })

  const { data: schedules, isFetching: isFetchingSchedules } = useQuery({
    enabled: !!providerId,
    queryKey: ['getProviderSchedules', providerId],
    queryFn: async () => {
      const data = await reactQueryRequest('getProviderSchedules', { providerId: User.providerId })
      return data
    },
  })

  const { data: slotsQueryData, isFetching: isFetchingSlotsData } = useQuery({
    enabled: !!slotsFilter?.startDate && !!slotsFilter?.endDate && !!shouldFetchSlots,
    queryKey: ['getProviderScheduleSlots', { ...(slotsFilter || {}), providerId }],
    queryFn: async () => {
      const data = await reactQueryRequest('getProviderScheduleSlots', {
        providerId: User.providerId,
        filter: slotsFilter,
      })

      return data?.slots || []
    },
  })

  const onDeleteError = useCallback((isSlot: boolean) => {
    toast.error(`Failed to delete ${isSlot ? 'Time slot' : 'Availability block'}. Try again later.`)
  }, [])

  const onDeleteSuccess = useCallback((isSlot: boolean) => {
    toast.success(`${isSlot ? 'Time slot' : 'Availability block'} has been successfully deleted`)
  }, [])

  const refetchSchedulesAndSlots = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['getProviderScheduleSlots'] })
    queryClient.invalidateQueries({ queryKey: ['getProviderSchedules'] })
    queryClient.invalidateQueries({ queryKey: ['getPendingVisitsForSchedule'] })
  }, [queryClient])

  const { mutate: deleteSlot, isPending: isDeletingSlot } = useMutation({
    mutationFn: async (slotId: string) => {
      setIsWaitingForPusherEvent(true)
      return await reactQueryRequest('deleteProviderScheduleSlot', { providerId, slotId })
    },
    onError: () => {
      onDeleteError(true)
    },
  })

  const { mutate: deleteSchedule, isPending: isDeletingSchedule } = useMutation({
    mutationFn: async (blockId: string) => {
      setIsWaitingForPusherEvent(true)
      return await reactQueryRequest('deleteProviderSchedule', { providerId, blockId })
    },
    onError: () => {
      onDeleteError(false)
    },
  })

  const slots = useMemo(() => {
    const now = new Date()
    const itemMap = new Map()

    // First add all slots (always keep them as base)
    const slotsArray = slotsQueryData || []
    slotsArray.forEach((slot) => {
      itemMap.set(slot.pendingVisitId || Symbol(), slot)
    })

    // Then add pending visits only if they're completed
    const pendingVisitsArray = pendingVisits || []
    pendingVisitsArray.forEach((visit) => {
      const startTime = new Date(visit.startTime)
      const endTime = new Date(startTime.getTime() + visit.duration * 60000)
      const isCompleted = endTime < now

      // Only replace slots with pending visits if they're fully completed
      if (isCompleted) {
        itemMap.set(visit.pendingVisitId, visit)
      }
    })

    return Array.from(itemMap.values())
  }, [pendingVisits, slotsQueryData])

  useEffect(() => {
    const removeListener = Pusher.listen((e) => {
      const error = e?.meta?.status === 'failed'
      switch (e?.event) {
        case 'schedule-created':
        case 'schedule-updated': {
          setIsWaitingForPusherEvent(false)
          if (error) {
            onErrorCreateOrUpdate?.()
          } else {
            onSuccessCreateOrUpdate?.()
            refetchSchedulesAndSlots()
          }
          break
        }
        case 'schedule-deleted': {
          setIsWaitingForPusherEvent(false)
          const isSlot = e?.meta?.delete_type === 'slot'
          if (error) {
            onDeleteError(isSlot)
          } else {
            onDeleteSuccess(isSlot)
            refetchSchedulesAndSlots()
          }
          break
        }
        case 'schedule-slot-updated': {
          refetchSchedulesAndSlots()
          break
        }
        case 'cancel-visit':
        case 'create-pending-visit':
        case 'update-pending-visit': {
          refetchSchedulesAndSlots()
          break
        }
      }
    })

    return () => {
      removeListener()
    }
  }, [onDeleteError, onDeleteSuccess, onErrorCreateOrUpdate, onSuccessCreateOrUpdate, refetchSchedulesAndSlots])

  return {
    submitSchedule,
    isSubmittingSchedule: !!isScheduleMutationPending || !!isWaitingForPusherEvent,
    schedules,
    isFetchingSchedules,
    slots,
    isSlotsFetching: isFetchingSlotsData || isFetchingPendingVisits,
    deleteSlot,
    deleteSchedule,
    isDeleting: !!isDeletingSchedule || !!isDeletingSlot || !!isWaitingForPusherEvent,
  }
}
